/**
 * Paginated list of returns for one bulk RMA action request.
 */
// eslint-disable-next-line
import { action, makeObservable, observable, runInAction } from 'mobx';

import { rootContainer, TYPE } from '../../../../../services/container/root';
import PagedDataStore, { IFetchListArgs, IPagedData } from '../../../../../stores/abstract-paged-data-store';
import TroubleshootingService, {
    IBulkRmaActionPaginatedRows,
    IBulkRmaActionReturnRow,
} from '../../../../../services/api/troubleshooting-service';

export class BulkRmaActionReturnsPagedListStore extends PagedDataStore<IBulkRmaActionReturnRow> {
    readonly perPage: number = 50;

    @observable storeId: string | number | null = null;
    @observable requestId: number | null = null;
    @observable returnStatusFilter: string = '';
    @observable reasonCodeFilter: string = '';
    @observable error: string | null = null;

    constructor() {
        super();
        makeObservable(this);
    }

    private get troubleshootingService(): TroubleshootingService {
        return rootContainer.get<TroubleshootingService>(TYPE.TroubleshootingService);
    }

    get isOpen(): boolean {
        return this.storeId != null && this.requestId != null;
    }

    @action
    async open(storeId: string | number, requestId: number): Promise<void> {
        this.storeId = storeId;
        this.requestId = requestId;
        this.returnStatusFilter = '';
        this.reasonCodeFilter = '';
        this.error = null;
        await this.loadPage(1);
    }

    @action
    close(): void {
        this.storeId = null;
        this.requestId = null;
        this.returnStatusFilter = '';
        this.reasonCodeFilter = '';
        this.error = null;
        this._list = [];
        this._total = 0;
        this._page = 1;
    }

    @action
    async setReturnStatusFilter(value: string): Promise<void> {
        this.returnStatusFilter = value;
        await this.loadPage(1);
    }

    @action
    async setReasonCodeFilter(value: string): Promise<void> {
        this.reasonCodeFilter = value;
        await this.loadPage(1);
    }

    private mapReturnsPageToPagedData(
        page: IBulkRmaActionPaginatedRows<IBulkRmaActionReturnRow>,
    ): IPagedData<IBulkRmaActionReturnRow> {
        const lastPage = page.last_page ?? Math.max(1, Math.ceil(page.total / page.per_page));
        const from = page.total === 0 ? 0 : (page.current_page - 1) * page.per_page + 1;
        const to = Math.min(page.current_page * page.per_page, page.total);

        return {
            data: page.data,
            current_page: page.current_page,
            per_page: page.per_page,
            total: page.total,
            last_page: lastPage,
            from,
            to,
            path: '',
            next_page_url: '',
        };
    }

    protected async fetchList(args: IFetchListArgs): Promise<IPagedData<IBulkRmaActionReturnRow> | undefined> {
        if (this.storeId == null || this.requestId == null) {
            return undefined;
        }

        try {
            const page = await this.troubleshootingService.listBulkRmaActionRequestReturns(
                this.storeId,
                this.requestId,
                {
                    returnStatus: this.returnStatusFilter || undefined,
                    reasonCode: this.reasonCodeFilter || undefined,
                    page: args.page,
                    perPage: args.perPage,
                },
            );
            runInAction(() => {
                this.error = null;
            });
            return this.mapReturnsPageToPagedData(page);
        } catch (err: any) {
            runInAction(() => {
                this.error = err?.response?.data?.error ?? err?.message ?? 'Failed to load returns';
            });
            return undefined;
        }
    }
}

export default new BulkRmaActionReturnsPagedListStore();
